import { Link } from "@components/Link/Link";
import { StyledContainer } from "@components/Styled/Container";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import { StyledH3, StyledH5 } from "@components/Styled/Text";
import { css } from "@emotion/react";
import React, { lazy, Suspense } from "react";
import SwiperCore, { Scrollbar } from "swiper";
import { SwiperSlide } from "swiper/react";
import tw from "twin.macro";
import { withCollectionButtonSlider } from "./withCollectionButtonSlider";
import Container from "@components/Atomic/Container/Container";

SwiperCore.use([Scrollbar]);

type ButtonProps = {
  slug: string;
};

type GliderWrapperProps = {
  alignment: string;
  count: number;
};

const Swiper = lazy(
  () => import(/* webpackChunkName: "swiper" */ "@components/Swiper/Swiper"),
);

const GliderWrapper = styledWithInvalidProps("div")(({
  alignment,
  count = 0,
}: GliderWrapperProps) => {
  if (alignment === "center" && count <= 7)
    return css`
      @media screen and (min-width: 1200px) {
        .swiper-wrapper {
          justify-content: center;
        }
      }
    `;
});

const Title = tw(StyledH3)``;

const SubTitle = tw(StyledH5)`
  mb-midi text-center
`;
const SectionHeader = tw.section`
  pt-mega px-micro text-center md:px-mega
`;
const SectionWrapper = tw.section`
  
`;
const Button = styledWithInvalidProps<ButtonProps>("div")(({ active }) => [
  tw`block text-center no-underline py-mini px-[1px] w-full hover:bg-black hover:text-white focus-visible:!outline-offset-[-1px]`,
  active ? tw`text-white bg-black` : tw`bg-tertiary text-primary`,
]);

const slides = {
  center: {
    480: {
      slidesPerView: 3.5,
      spaceBetween: 8,
    },
    640: {
      slidesPerView: 4.5,
      spaceBetween: 8,
    },
    1024: {
      slidesPerView: 6.5,
      spaceBetween: 8,
    },
    1200: {
      slidesPerView: 8,
      spaceBetween: 8,
    },
  },
  left: {
    480: {
      slidesPerView: 3.5,
      spaceBetween: 10,
    },
    640: {
      slidesPerView: 4.5,
      spaceBetween: 10,
    },
    1024: {
      slidesPerView: 6.5,
      spaceBetween: 10,
    },
    1200: {
      slidesPerView: 8.5,
      spaceBetween: 10,
    },
  },
};

const CollectionButtonSlider = withCollectionButtonSlider(
  ({
    isActiveCollection,
    titleLink,
    subtitle,
    sectionLinks,
    alignment,
    styles,
  }) => {
    return (
      <Container
        styles={styles}
        data-component-type="section-collection-button-slider"
      >
        <StyledContainer width="xl">
          {titleLink?.title ? (
            <SectionHeader>
              <Title>
                <Link to={titleLink?.url} className="no-underline">
                  {titleLink?.title}
                </Link>
              </Title>
              <SubTitle>{subtitle}</SubTitle>
            </SectionHeader>
          ) : null}
          <SectionWrapper>
            <GliderWrapper alignment={alignment} count={sectionLinks?.length}>
              <Suspense fallback={<></>}>
                <Swiper
                  slidesPerView={2.5}
                  spaceBetween={10}
                  scrollbar={{ draggable: true }}
                  style={{ paddingBottom: "1.25rem" }}
                  observer={true}
                  observeParents={true}
                  breakpoints={{
                    480: {
                      ...slides[alignment ?? "left"]["480"],
                      observer: true,
                      observeParents: true,
                    },
                    640: {
                      ...slides[alignment ?? "left"]["640"],
                      observer: true,
                      observeParents: true,
                    },
                    1024: {
                      ...slides[alignment ?? "left"]["1024"],
                      observer: true,
                      observeParents: true,
                    },
                    1200: {
                      ...slides[alignment ?? "left"]["1200"],
                      observer: true,
                      observeParents: true,
                    },
                  }}
                >
                  {sectionLinks?.map((section: any, index: any) => (
                    <SwiperSlide key={index}>
                      <Button
                        {...section}
                        as={section.link?.url ? Link : null}
                        to={section.link?.url}
                        active={isActiveCollection(
                          section.link?.url,
                          section.link?.title,
                        )}
                        alignment={alignment}
                      >
                        {section.title}
                      </Button>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Suspense>
            </GliderWrapper>
          </SectionWrapper>
        </StyledContainer>
      </Container>
    );
  },
);
export default CollectionButtonSlider;
